import { type ChangeEventHandler, forwardRef, useRef } from 'react'
import classNames from 'classnames'

import { componentsWebColors } from '../../tailwind/componentsWebColors'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'

type SizeType = 'small' | 'big'

type Props = Omit<React.HTMLProps<HTMLInputElement>, 'inputMode' | 'size' | 'value' | 'onChange'> & {
  label?: string
  wrapperClassName?: string
  description?: string
  indicatorColor?: string
  checkedColor?: string
  size?: SizeType
  indeterminate?: boolean
  labelClassName?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * @group Components
 * @category Component
 */
const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      wrapperClassName,
      labelClassName,
      description,
      label,
      indeterminate,
      indicatorColor = '#FFFFFF',
      checkedColor,
      disabled,
      checked,
      onChange,
      size = 'small',
      id = 'checkbox',
      ...restProps
    },
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement>(null!)

    // Use the forwarded ref if provided, otherwise fallback to the internal ref
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || innerRef

    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate
    }

    const handleOnChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      event.stopPropagation()
      onChange?.(event)
    }

    return (
      <span className={classNames('relative flex items-center', wrapperClassName)} onClick={(e) => e.stopPropagation()}>
        <input
          id={id}
          type="checkbox"
          onChange={handleOnChange}
          disabled={disabled}
          checked={checked}
          className={resolveTailwindClasses(
            'peer relative shrink-0 appearance-none rounded-[4px] border border-primary-500',
            size === 'small' ? 'h-4 w-4' : 'mt-[-2px] h-5 w-5',
            !disabled &&
              'border-primary-500 checked:bg-primary-500 indeterminate:bg-primary-500 hover:border-primary-400 checked:hover:bg-primary-400 indeterminate:hover:bg-primary-400 active:border-2',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            className
          )}
          style={{ backgroundColor: checked ? checkedColor : undefined }}
          ref={inputRef}
          {...restProps}
        />
        {label && (
          <div className="ml-2 flex flex-col justify-center text-sm">
            {label && (
              <label htmlFor={id} className={resolveTailwindClasses(labelClassName)}>
                {label}
              </label>
            )}
            {description && <span className="text-neutral-500">{description}</span>}
          </div>
        )}
        {indeterminate ? (
          <svg
            className={classNames(
              'pointer-events-none absolute mt-[2px] hidden h-3 w-3 peer-checked:block peer-indeterminate:block',
              size === 'big' ? 'left-1' : 'left-0.5'
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M2.91797 7H11.0846"
              stroke={disabled ? componentsWebColors.neutral[200] : indicatorColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            className={classNames(
              'pointer-events-none absolute left-1 hidden h-3 w-3 peer-checked:block',
              size === 'big' ? 'left-1' : 'left-[2px]'
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke={disabled ? componentsWebColors.neutral[200] : indicatorColor}
              strokeWidth="1.6666"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export { Checkbox }
